/**
 * Planner Service
 * Handles communication with the planner agent for presentation planning
 * 
 * Architecture (following taskpaneChatService pattern):
 * - Each planning job has its own conversation stored in GlobalStateManager
 * - PlannerAgent is registered in the conversation's AgentRegistry
 * - Version history is managed via singleton PlannerVersionHistoryManager per jobId
 * 
 * According to Notion docs: Planning Modal instantiates Planner Agent (100% exclusive usage)
 */

import { PlannerAgent, type Plan } from "../../main/agents/planner";
import GlobalStateManager from "../../main/globals/global-state/globalStateManager";
import { authService } from "@carousel-office/shared";
import { initializeAuthIntegration } from "../../main/globals/authIntegration";
import { PresentationPlan, Slide, Section } from "@carousel-office/shared/types/planning";
import { 
  getPlannerVersionHistoryManager, 
  removePlannerVersionHistoryManager,
  type PlanVersion 
} from "../../main/core/plannerVersionHistoryManager";
import { AgentCallbacks } from "../../main/core/types";
import { UserInterviewTool } from "../../main/agents/tools/userInterview";
import { generateConversationId } from "../../main/utils/idGenerator";

// Style guide type used for planning
export type PlannerStyleGuide = {
  type: 'live' | 'uploaded';
  documentId: string;
  path?: string;
};

let isServiceInitialized = false;

// Current style guide - set when creating a planning conversation
let currentStyleGuide: PlannerStyleGuide | null = null;

// Current job ID for the active planning session
let currentJobId: string | null = null;

// Event listener cleanup function for interview submissions
let plannerInterviewCleanup: (() => void) | null = null;

// ═══════════════════════════════════════════════════════════════════════════
// PLAN CONVERSION UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Changes object returned by editPlan tool
 */
interface PlanChanges {
  added: string[];
  removed: string[];
  modified: string[];
  reordered: string[];
}

/**
 * Convert Plan (ppt-addin format) to PresentationPlan (shared format)
 * Sets _pending* flags based on the changes object (if provided)
 * 
 * For deleted items:
 * 1. First tries ID-based matching using changes.removed array
 * 2. Falls back to title-based matching if IDs don't match
 * 
 * Deleted items are restored with _pendingDeletion: true so the UI
 * can show them with deletion styling and accept/reject buttons.
 */
function convertPlanToPresentationPlan(
  plan: Plan, 
  planId?: string,
  changes?: PlanChanges,
  previousPlan?: PresentationPlan
): PresentationPlan {
  const now = Date.now();
  
  // Create sets for efficient lookup
  const addedIds = new Set(changes?.added || []);
  const modifiedIds = new Set(changes?.modified || []);
  const removedIds = new Set(changes?.removed || []);
  const hasRemovals = removedIds.size > 0;
  
  console.log('[PlannerService] Converting plan with changes:', {
    added: changes?.added?.length ?? 0,
    modified: changes?.modified?.length ?? 0,
    removed: changes?.removed?.length ?? 0,
    removedIds: Array.from(removedIds),
    hasPreviousPlan: !!previousPlan
  });
  
  // Build sets of IDs and titles in the new plan for comparison
  const newPlanSlideIds = new Set<string>();
  const newPlanSlideTitles = new Set<string>();
  const newPlanSectionIds = new Set<string>();
  const newPlanSectionTitles = new Set<string>();
  plan.sections.forEach(section => {
    if (section.id) newPlanSectionIds.add(section.id);
    newPlanSectionTitles.add(section.title.toLowerCase().trim());
    section.slides.forEach(slide => {
      if (slide.id) newPlanSlideIds.add(slide.id);
      newPlanSlideTitles.add(slide.title.toLowerCase().trim());
    });
  });
  
  // Track which deleted items we've already restored (to avoid duplicates)
  const restoredSlideIds = new Set<string>();
  const restoredSectionIds = new Set<string>();
  
  // Convert the new plan sections
  const convertedSections: Section[] = plan.sections.map((section, sectionIndex) => {
    const sectionId = section.id || `section_${sectionIndex}`;
    const isSectionAdded = addedIds.has(sectionId);
    const isSectionModified = modifiedIds.has(sectionId);
    
    // Convert slides
    const convertedSlides: Slide[] = section.slides.map((slide, slideIndex) => {
      const slideId = slide.id || `slide_${slideIndex}`;
      const isSlideAdded = addedIds.has(slideId);
      const isSlideModified = modifiedIds.has(slideId);
      
      const slideObj: Slide = {
        id: slideId,
        name: slide.title,
        purpose: slide.purpose,
        order: slide.index ?? slideIndex,
      };
      // Set pending flags based on changes
      if (isSlideAdded) slideObj._pendingInsertion = true;
      if (isSlideModified) slideObj._pendingUpdate = true;
      return slideObj;
    });
    
    // If we have a previous plan and there were removals, find deleted slides
    if (previousPlan && hasRemovals) {
      // Try to find matching section in previousPlan by ID first, then by title
      let previousSection = previousPlan.sections.find(s => s.id === sectionId);
      if (!previousSection) {
        // Fallback to title matching
        previousSection = previousPlan.sections.find(
          s => s.name.toLowerCase().trim() === section.title.toLowerCase().trim()
        );
      }
      
      if (previousSection) {
        previousSection.slides.forEach(oldSlide => {
          // Check if this slide was removed (by ID from changes.removed)
          const wasRemovedById = removedIds.has(oldSlide.id);
          
          // Also check if slide exists in new plan (by ID or title)
          const existsById = newPlanSlideIds.has(oldSlide.id);
          const existsByTitle = convertedSlides.some(
            s => s.name.toLowerCase().trim() === oldSlide.name.toLowerCase().trim()
          );
          
          // If removed by ID, or if it doesn't exist in new plan
          if ((wasRemovedById || (!existsById && !existsByTitle)) && !restoredSlideIds.has(oldSlide.id)) {
            console.log('[PlannerService] Restoring deleted slide:', oldSlide.name, 'id:', oldSlide.id);
            restoredSlideIds.add(oldSlide.id);
            convertedSlides.push({
              id: oldSlide.id,
              name: oldSlide.name,
              purpose: oldSlide.purpose,
              order: oldSlide.order,
              _pendingDeletion: true,
            });
          }
        });
      }
    }
    
    const sectionObj: Section = {
      id: sectionId,
      name: section.title,
      purpose: section.purpose,
      slides: convertedSlides,
      isCollapsed: false,
      order: sectionIndex,
    };
    // Set pending flags based on changes
    if (isSectionAdded) sectionObj._pendingInsertion = true;
    if (isSectionModified) sectionObj._pendingUpdate = true;
    return sectionObj;
  });
  
  // If we have a previous plan and there were removals, find deleted sections
  if (previousPlan && hasRemovals) {
    previousPlan.sections.forEach(oldSection => {
      // Check if this section was removed (by ID from changes.removed)
      const wasRemovedById = removedIds.has(oldSection.id);
      
      // Also check if section exists in new plan (by ID or title)
      const existsById = newPlanSectionIds.has(oldSection.id);
      const existsByTitle = convertedSections.some(
        s => s.name.toLowerCase().trim() === oldSection.name.toLowerCase().trim()
      );
      
      // If removed by ID, or if it doesn't exist in new plan
      if ((wasRemovedById || (!existsById && !existsByTitle)) && !restoredSectionIds.has(oldSection.id)) {
        console.log('[PlannerService] Restoring deleted section:', oldSection.name, 'id:', oldSection.id);
        restoredSectionIds.add(oldSection.id);
        const deletedSection: Section = {
          id: oldSection.id,
          name: oldSection.name,
          purpose: oldSection.purpose,
          slides: oldSection.slides.map(slide => ({
            ...slide,
            _pendingDeletion: true,
          })),
          isCollapsed: false,
          order: oldSection.order,
          _pendingDeletion: true,
        };
        convertedSections.push(deletedSection);
      }
    });
  }
  
  return {
    id: planId || `plan_${Date.now()}`,
    slideCount: plan.totalSlides || 0,
    sections: convertedSections,
    version: 1,
    metadata: {
      createdAt: now,
      lastModified: now,
    },
  };
}

/**
 * Convert PresentationPlan (shared format) to Plan (ppt-addin format)
 */
function convertPresentationPlanToPlan(presentationPlan: PresentationPlan): Plan {
  return {
    sections: presentationPlan.sections.map((section) => ({
      id: section.id,
      title: section.name,
      purpose: section.purpose,
      slides: section.slides.map((slide) => ({
        id: slide.id,
        title: slide.name,
        purpose: slide.purpose,
        index: slide.order,
      })),
    })),
    totalSlides: presentationPlan.slideCount,
    totalSections: presentationPlan.sections.length,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// SERVICE INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Initialize the planner service
 * Sets up GlobalStateManager and gets the current presentation ID
 */
export async function initializePlannerService(): Promise<void> {
  try {
    console.log('[PlannerService] Initializing...');
    
    // Get JWT token from auth service
    await authService.reloadFromStorage();
    const jwtToken = authService.getAccessToken();
    
    if (!jwtToken) {
      throw new Error('JWT token required for Keywords API. Please ensure you are authenticated.');
    }
    
    // Initialize GlobalStateManager
    const globalState = GlobalStateManager.getInstance();
    await globalState.initialize(jwtToken);
    
    // Initialize auth integration
    await initializeAuthIntegration();
    
    // Set up event listener for interview submissions from UI
    const handleInterviewSubmit = (event: Event) => {
      const customEvent = event as CustomEvent<{ interviewId: string; answers: unknown[] }>;
      console.log('[PlannerService] Interview submit event received:', customEvent.detail);
      UserInterviewTool.submitAnswers(customEvent.detail.answers as Parameters<typeof UserInterviewTool.submitAnswers>[0]);
    };
    window.addEventListener('carousel:interview-submit', handleInterviewSubmit);
    
    // Set up event listener for interview submissions from INSIDE agents
    const handleAgentInterviewSubmit = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        interviewId: string; 
        agentToolId: string; 
        toolCallId: string; 
        answers: unknown[] 
      }>;
      console.log('[PlannerService] Agent interview submit event received:', customEvent.detail);
      UserInterviewTool.submitAnswers(customEvent.detail.answers as Parameters<typeof UserInterviewTool.submitAnswers>[0]);
    };
    window.addEventListener('carousel:agent-interview-submit', handleAgentInterviewSubmit);
    
    plannerInterviewCleanup = () => {
      window.removeEventListener('carousel:interview-submit', handleInterviewSubmit);
      window.removeEventListener('carousel:agent-interview-submit', handleAgentInterviewSubmit);
    };
    
    isServiceInitialized = true;
    console.log('[PlannerService] Initialized successfully');
  } catch (error) {
    console.error('[PlannerService] Initialization failed:', error);
    isServiceInitialized = false;
    // Cleanup event listener if set
    if (plannerInterviewCleanup) {
      plannerInterviewCleanup();
      plannerInterviewCleanup = null;
    }
    throw error;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CONVERSATION MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a new planning conversation with its own PlannerAgent instance
 * The conversation is registered in GlobalStateManager with origin 'planning'
 * 
 * The styleGuide.documentId is used as the presentationId in the conversation,
 * as it represents the template deck being used for styling reference.
 * 
 * @param styleGuide - The style guide configuration with the template documentId
 * @param jobId - Optional unique job ID for the planning session (auto-generated if not provided)
 * @returns The created conversation's jobId
 */
export function createPlanningConversation(
  styleGuide: PlannerStyleGuide,
  jobId?: string
): string {
  if (!isServiceInitialized) {
    throw new Error('Planner service not initialized. Call initializePlannerService() first.');
  }

  const globalState = GlobalStateManager.getInstance();
  
  // Generate job ID if not provided
  if (!jobId) {
    const user = authService.getCurrentUser();
    if (!user?._id) {
      throw new Error('User ID is required to create a planning conversation');
    }
    jobId = generateConversationId(user._id);
  }
  const planJobId = jobId;
  
  // Check if conversation already exists
  const existingConversation = globalState.getConversation(planJobId);
  if (existingConversation) {
    console.log(`[PlannerService] Reusing existing conversation for job ${planJobId}`);
    currentJobId = planJobId;
    currentStyleGuide = styleGuide;
    return planJobId;
  }
  
  // Create the conversation using styleGuide.documentId as the presentationId
  // This ties the planning session to the template deck being used for styling
  const conversation = globalState.createConversation(
    planJobId,
    styleGuide.documentId,
    'planning'
  );
  
  // Create a new PlannerAgent instance for this conversation
  const plannerAgent = new PlannerAgent();
  
  // Register the planner in the conversation's AgentRegistry
  conversation.agentRegistry.registerAgent('planner', plannerAgent);
  
  // Set current job ID and style guide
  currentJobId = planJobId;
  currentStyleGuide = styleGuide;
  
  console.log(`[PlannerService] Created planning conversation ${planJobId} with styleGuide documentId: ${styleGuide.documentId}`);
  
  return planJobId;
}

/**
 * Get the PlannerAgent for a specific job
 * Routes through GlobalStateManager to get the correct agent instance
 */
function getPlannerAgent(jobId: string): PlannerAgent {
  const globalState = GlobalStateManager.getInstance();
  const conversation = globalState.getConversation(jobId);
  
  if (!conversation) {
    throw new Error(`Planning conversation ${jobId} not found. Call createPlanningConversation() first.`);
  }
  
  if (conversation.conversationOrigin !== 'planning') {
    throw new Error(`Conversation ${jobId} is not a planning conversation (origin: ${conversation.conversationOrigin})`);
  }
  
  const planner = conversation.agentRegistry.getAgent<PlannerAgent>('planner');
  
  if (!planner) {
    throw new Error(`No planner agent found for job ${jobId}`);
  }
  
  return planner;
}

// ═══════════════════════════════════════════════════════════════════════════
// MESSAGE HANDLING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Send a message to the planner agent for a specific job
 * Routes through GlobalStateManager to get the correct agent instance
 * 
 * @param message - The user message to send (used as initialPrompt)
 * @param options - Optional parameters for planning
 * @param callbacks - Optional callbacks for streaming and operation updates
 * @returns The full response from the planner
 */
export async function sendMessageToPlanner(
  message: string,
  options?: {
    slideCount?: number;
    jobId?: string;
    currentPlan?: PresentationPlan;
    styleGuide?: {
      type: 'live' | 'uploaded';
      documentId?: string;
      path?: string;
    };
    /** Parsed style guide content - layout and text info for LLM context */
    parsedStyleGuide?: {
      slides: Array<{
        slide_index: number;
        layout_id?: string | null;
        layout_name?: string | null;
        shapes: Array<{
          shape_id: string;
          shape_type: string;
          position: { top: number; left: number; width: number; height: number };
          text?: string | null;
        }>;
      }>;
      layouts_used: Array<{ layout_id: string; layout_name?: string | null; count: number }>;
      sections: Array<{ name: string; start_index: number; end_index: number }>;
    };
  },
  callbacks?: AgentCallbacks & {
    onPlanChange?: (plan: PresentationPlan, action: 'created' | 'updated') => void;
  }
): Promise<{ 
  content: string; 
  plan?: PresentationPlan;
  action: 'plan_created' | 'plan_updated' | 'conversation' | 'error';
  metadata?: {
    totalSlides?: number;
    totalSections?: number;
    changes?: {
      added: string[];
      removed: string[];
      modified: string[];
      reordered: string[];
    };
  };
}> {
  if (!isServiceInitialized) {
    throw new Error('Planner service not initialized. Call initializePlannerService() first.');
  }

  // Get job ID - must have a conversation already created via createPlanningConversation
  const jobId = options?.jobId || currentJobId;
  
  if (!jobId) {
    throw new Error('No active planning job. Call createPlanningConversation() first with a styleGuide.');
  }
  
  // Verify conversation exists
  const globalState = GlobalStateManager.getInstance();
  if (!globalState.getConversation(jobId)) {
    throw new Error(`Planning conversation ${jobId} not found. Call createPlanningConversation() first.`);
  }
  
  // Update current job if different
  if (jobId !== currentJobId) {
    currentJobId = jobId;
  }
  
  const plannerAgent = getPlannerAgent(jobId);

  try {
    console.log(`[PlannerService] Sending message to job ${jobId}:`, message);
    
    // Get the current plan from the agent BEFORE executing (this is our "previous plan" for tracking deletions)
    const agentCurrentPlan = plannerAgent.getPlan();
    const previousPlanForTracking: PresentationPlan | undefined = agentCurrentPlan 
      ? convertPlanToPresentationPlan(agentCurrentPlan)
      : options?.currentPlan;
    
    console.log('[PlannerService] Previous plan for deletion tracking:', {
      hasAgentPlan: !!agentCurrentPlan,
      hasOptionsPlan: !!options?.currentPlan,
      previousPlanSections: previousPlanForTracking?.sections?.length ?? 0
    });
    
    // Convert PresentationPlan to Plan if provided
    const currentPlan = options?.currentPlan 
      ? convertPresentationPlanToPlan(options.currentPlan)
      : undefined;
    
    // Use provided styleGuide or the current one stored when conversation was created
    const styleGuide = options?.styleGuide || currentStyleGuide;
    if (!styleGuide) {
      throw new Error('No styleGuide available. Ensure createPlanningConversation() was called with a styleGuide.');
    }
    
    // Build parameters per Notion schema
    const params = {
      initialPrompt: message,
      slideCount: options?.slideCount || 30,
      jobId,
      currentPlan,
      styleGuide,
      parsedStyleGuide: options?.parsedStyleGuide,
    };
    
    console.log('[PlannerService] Built params for planner:', {
      hasInitialPrompt: !!params.initialPrompt,
      slideCount: params.slideCount,
      jobId: params.jobId,
      hasCurrentPlan: !!params.currentPlan,
      styleGuideType: params.styleGuide?.type,
      hasParsedStyleGuide: !!params.parsedStyleGuide,
      parsedStyleGuideSlideCount: params.parsedStyleGuide?.slides?.length || 0,
      parsedStyleGuideLayoutsCount: params.parsedStyleGuide?.layouts_used?.length || 0,
      parsedStyleGuideSectionsCount: params.parsedStyleGuide?.sections?.length || 0,
    });
    
    // Execute planner with callbacks
    const result = await plannerAgent.execute(
      params,
      {
        onChunk: callbacks?.onChunk,
        onToolCall: callbacks?.onToolCall,
        onToolResult: (toolName: string, result: unknown) => {
          // Forward to provided callback
          callbacks?.onToolResult?.(toolName, result);
          
          // Handle plan changes
          if (callbacks?.onPlanChange && (toolName === 'createPlan' || toolName === 'editPlan')) {
            const toolResult = result as { success?: boolean; plan?: Plan; changes?: PlanChanges };
            if (toolResult.success && toolResult.plan) {
              // Pass changes and previous plan to conversion so flags are set correctly
              // For deleted items, they'll be restored from previousPlanForTracking with _pendingDeletion: true
              const presentationPlan = convertPlanToPresentationPlan(
                toolResult.plan, 
                undefined, 
                toolResult.changes,
                previousPlanForTracking // Use the plan we captured BEFORE execution
              );
              const action = toolName === 'createPlan' ? 'created' : 'updated';
              callbacks.onPlanChange(presentationPlan, action);
            }
          }
        }
      }
    );
    
    console.log('[PlannerService] Received response:', result);
    
    // Convert Plan to PresentationPlan if present
    const presentationPlan = result.plan 
      ? convertPlanToPresentationPlan(result.plan)
      : undefined;
    
    return {
      content: result.message,
      plan: presentationPlan,
      action: result.action,
      metadata: result.metadata,
    };
  } catch (error) {
    console.error('[PlannerService] Error sending message:', error);
    throw error;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PLAN ACCESS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the current plan from the planner agent
 * Used for handoff to Multi-Slide Builder
 */
export function getCurrentPlan(): PresentationPlan | null {
  if (!isServiceInitialized || !currentJobId) {
    return null;
  }
  
  try {
    const plannerAgent = getPlannerAgent(currentJobId);
    const plan = plannerAgent.getPlan();
    if (!plan) {
      return null;
    }
    return convertPlanToPresentationPlan(plan);
  } catch (error) {
    console.warn('[PlannerService] Could not get current plan:', error);
    return null;
  }
}

/**
 * Set the current plan in the planner agent
 * Useful when resuming a paused planning session
 */
export function setCurrentPlan(plan: PresentationPlan): void {
  if (!isServiceInitialized || !currentJobId) {
    throw new Error('Planner service not initialized or no active job.');
  }
  
  const plannerAgent = getPlannerAgent(currentJobId);
  const pptPlan = convertPresentationPlanToPlan(plan);
  plannerAgent.setPlan(pptPlan);
}

// ═══════════════════════════════════════════════════════════════════════════
// SERVICE LIFECYCLE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if the planner service is initialized
 */
export function isPlannerServiceInitialized(): boolean {
  return isServiceInitialized;
}

/**
 * Reset/cleanup the planner service
 * Called when planning is finalized and agent is no longer needed
 * @param cleanupVersionHistory - If true, also removes the version history manager for the job
 */
export function resetPlannerService(cleanupVersionHistory: boolean = false): void {
  console.log('[PlannerService] Resetting planner service');
  
  // Optionally cleanup version history (usually keep it for resume)
  if (cleanupVersionHistory && currentJobId) {
    removePlannerVersionHistoryManager(currentJobId);
    console.log(`[PlannerService] Removed version history for job ${currentJobId}`);
  }
  
  // Remove conversation from GlobalStateManager
  if (currentJobId) {
    const globalState = GlobalStateManager.getInstance();
    globalState.removeConversation(currentJobId);
    console.log(`[PlannerService] Removed conversation for job ${currentJobId}`);
  }
  
  // Cleanup interview event listener
  if (plannerInterviewCleanup) {
    plannerInterviewCleanup();
    plannerInterviewCleanup = null;
  }
  
  isServiceInitialized = false;
  currentJobId = null;
  currentStyleGuide = null;
}

/**
 * End a specific planning job without resetting the entire service
 * @param jobId - The job ID to end
 * @param cleanupVersionHistory - If true, also removes the version history manager
 */
export function endPlanningJob(jobId: string, cleanupVersionHistory: boolean = false): void {
  console.log(`[PlannerService] Ending planning job ${jobId}`);
  
  if (cleanupVersionHistory) {
    removePlannerVersionHistoryManager(jobId);
  }
  
  const globalState = GlobalStateManager.getInstance();
  globalState.removeConversation(jobId);
  
  if (currentJobId === jobId) {
    currentJobId = null;
    currentStyleGuide = null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// VERSION HISTORY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the version history for the current planning job
 * Directly accesses the singleton PlannerVersionHistoryManager
 * @returns Array of plan versions, newest first
 */
export function getVersionHistory(): PlanVersion[] {
  if (!currentJobId) {
    return [];
  }
  
  const manager = getPlannerVersionHistoryManager(currentJobId);
  return manager.getVersionHistory();
}

/**
 * Restore plan to a specific version
 * Uses the singleton PlannerVersionHistoryManager and syncs with the agent
 * @param versionNumber - The version number to restore (1-indexed)
 * @returns The restored plan as PresentationPlan, or null if failed
 */
export function restoreVersion(versionNumber: number): PresentationPlan | null {
  if (!currentJobId) {
    console.warn('[PlannerService] Cannot restore version - no active job');
    return null;
  }
  
  try {
    const plannerAgent = getPlannerAgent(currentJobId);
    const manager = getPlannerVersionHistoryManager(currentJobId);
    const restoredPlan = manager.restoreVersion(versionNumber) as Plan;
    
    // Sync the restored plan with the agent
    plannerAgent.setPlan(restoredPlan);
    
    return convertPlanToPresentationPlan(restoredPlan);
  } catch (error) {
    console.error('[PlannerService] Failed to restore version:', error);
    return null;
  }
}

/**
 * Get the current version number
 */
export function getCurrentVersionNumber(): number {
  if (!currentJobId) {
    return 0;
  }
  
  const manager = getPlannerVersionHistoryManager(currentJobId);
  return manager.getCurrentVersionNumber();
}

/**
 * Get diff between two versions
 */
export function getVersionDiff(fromVersion: number, toVersion: number) {
  if (!currentJobId) {
    return null;
  }
  
  try {
    const manager = getPlannerVersionHistoryManager(currentJobId);
    return manager.getDiff(fromVersion, toVersion);
  } catch (error) {
    console.error('[PlannerService] Failed to get version diff:', error);
    return null;
  }
}

/**
 * Get the current job ID
 */
export function getCurrentJobId(): string | null {
  return currentJobId;
}

/**
 * Get the current style guide
 */
export function getCurrentStyleGuide(): PlannerStyleGuide | null {
  return currentStyleGuide;
}

/**
 * Resume an existing planning job
 * Requires the styleGuide to recreate the conversation if it was cleaned up
 * 
 * @param jobId - The job ID to resume
 * @param styleGuide - The style guide to use (required if conversation doesn't exist)
 */
export function resumePlanningJob(jobId: string, styleGuide: PlannerStyleGuide): void {
  const globalState = GlobalStateManager.getInstance();
  const conversation = globalState.getConversation(jobId);
  
  if (!conversation) {
    // Recreate the conversation (resume scenario after cleanup)
    createPlanningConversation(styleGuide, jobId);
  } else {
    currentJobId = jobId;
    currentStyleGuide = styleGuide;
  }
}
